import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Tree } from "react-arborist";
import { Card, CardBody } from "reactstrap";
import { getJwt } from '../../helpers/clearnet_api_helper';
// import ResizeObserver if you want to support older browsers
// import ResizeObserver from 'resize-observer-polyfill';



// NODE RENDERER
const Node = ({ node, style, dragHandle }) => {
  const caretWidth = 20; // px, adjust as needed to match your caret's width

  let caret = null;
  if (node.isInternal && node.children && node.children.length > 0) {
    caret = (
      <span
        style={{ display: 'inline-block', width: caretWidth, marginRight: 4, fontSize: '1rem', textAlign: 'center' }}
        onClick={(e) => {
          e.stopPropagation();
          node.toggle();
        }}
      >
        <i className={node.isOpen ? "fa fa-caret-down" : "fa fa-caret-right"} />
      </span>
    );
  } else {
    // Empty placeholder to reserve space
    caret = <span style={{ display: 'inline-block', width: caretWidth, marginRight: 4 }} />;
  }

  return (
    <div
      ref={dragHandle}
      style={{
        ...style, // <--- CRITICAL: This provides the "tabbing to the right"
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        paddingRight: '8px',
        borderRadius: '4px',
        // Highlight logic
        backgroundColor: node.isSelected ? '#f0f0f0' : 'transparent',
        transition: 'background 0.1s ease',
      }}
    >
      {/* Caret icon - toggles expand/collapse without changing selection */}
      {caret}
      {/* Folder/File icon + Node name - selects the node */}
      <span
        style={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden' }}
        onClick={(e) => {
          e.stopPropagation();
          node.select();
        }}
      >
        <span style={{ marginRight: '8px' }}>
          {node.isInternal ? (node.isOpen ? '📂' : '📁') : '📄'}
        </span>
        <span style={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontWeight: 400,
        }}>
          {node.data.name}
        </span>
      </span>
    </div>
  );
};

// Helper to find the path (array of ids) from root to target node
function findPathToNode(nodes, targetId, path = []) {
  for (const node of nodes) {
    const newPath = [...path, String(node.id)];
    if (String(node.id) === String(targetId)) return newPath;
    if (node.children) {
      const childPath = findPathToNode(node.children, targetId, newPath);
      if (childPath) return childPath;
    }
  }
  return null;
}

///////////////////////////
// FolderTree Component
///////////////////////////
const FolderTree = forwardRef((props, ref) => {   //use forwardRef to allow parent to call methods on Tree (forceRefresh)
  const {
    currentId,
    onFolderChange,
    sidebarWidth,
    minSidebarWidth = 180,
    maxSidebarWidth = 500
  } = props;

  const [folders, setFolders] = useState(null);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const treeRef = useRef(null);
  const didInitialize = useRef(false);
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const [refreshFlag, setRefreshFlag] = useState(false);


  // Expose a forceRefresh method to parent
  useImperativeHandle(ref, () => ({
    forceRefresh: () => setRefreshFlag(flag => !flag)
  }));

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await getJwt(process.env.REACT_APP_CSHUB_API_URL_FOLDER_TREE); // Fetch folder tree from backend
        console.log('folder tree response:', response);  // check this

        const raw = Array.isArray(response) ? response
          : Array.isArray(response?.FolderTree) ? response.FolderTree
          : [];

        // Normalize PascalCase (AWS) to camelCase (local) recursively
        const normalize = (nodes) => nodes.map(node => ({
          id: node.id ?? node.Id,
          name: node.name ?? node.Name,
          isFolder: node.isFolder ?? node.IsFolder,
          parentId: node.parentId ?? node.ParentId,
          children: normalize(node.children ?? node.Children ?? []),
        }));
        const data = normalize(raw);
        setFolders(data);

        //setFolders(response || []);
        setError(null);


      } catch (error) {
        setFolders([]);
        setError(error?.message || 'Failed to load folders');
      }
    };
    loadData();
  }, [currentId, refreshFlag]);

  useEffect(() => {

    if (!folders || folders.length === 0 || !treeRef.current) return;

    if (!didInitialize.current) {
      if (currentId) {
        // Expand the path to the node with currentId
        const path = findPathToNode(folders, currentId);
        if (path) {
          path.slice(0, -1).forEach(id => treeRef.current.open(id));
          setSelectedId(String(currentId));
        }
      } else {
        // No currentId, just open root
        treeRef.current.closeAll();
        const rootId = String(folders[0].id);
        treeRef.current.open(rootId);
        setSelectedId(rootId);
      }
      didInitialize.current = true;
      return;
    }

    // After initial load, run only when currentId changes
    if (didInitialize.current) {
      if (!currentId) {
        const rootId = String(folders[0].id);
        treeRef.current.open(rootId);
        setSelectedId(rootId);
      } else {
        const path = findPathToNode(folders, currentId);
        if (path) {
          path.slice(0, -1).forEach(id => treeRef.current.open(id));
          setSelectedId(String(currentId));
        }
      }
    }
  }, [folders, currentId]);

  // Sync selectedId with currentId from URL
  useEffect(() => {
    if (currentId) {
      setSelectedId(String(currentId));
    }
  }, [currentId]);

  useEffect(() => {
    let rafId = null;
    function updateHeight() {
      if (containerRef.current) {
        const height = containerRef.current.offsetHeight;
        rafId = requestAnimationFrame(() => {
          setContainerHeight(height);
        });
      }
    }
    updateHeight();

    // Use ResizeObserver for dynamic resizing
    const resizeObserver = new window.ResizeObserver(updateHeight);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => {
      resizeObserver.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

// Inline style for Webkit scrollbars (Chrome, Edge, Safari)
const folderTreeScrollbarStyle = `
.filemanager-sidebar::-webkit-scrollbar {
  width: 8px;
  background: #23263a;
}
.filemanager-sidebar::-webkit-scrollbar-thumb {
  background: #444a6d;
  border-radius: 4px;
}
.filemanager-sidebar::-webkit-scrollbar-thumb:hover {
  background: #5a5f85;
}
`;

// Inject style tag once
if (typeof document !== 'undefined' && !document.getElementById('foldertree-scrollbar-style')) {
  const style = document.createElement('style');
  style.id = 'foldertree-scrollbar-style';
  style.innerHTML = folderTreeScrollbarStyle;
  document.head.appendChild(style);
}
  return (
    <React.Fragment>
      <Card
        className="filemanager-sidebar me-md-2 shadow-none border-0"
        style={{
          height: '100%',
          minHeight: 0,
          width: sidebarWidth,
          minWidth: minSidebarWidth,
          maxWidth: maxSidebarWidth,
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.1s',
          //background: '#fff',
          padding: '24px 0 24px 24px',
          boxSizing: 'border-box',
          //scrollbarColor: '#6a6b6b #e4e2e2ef', // For Firefox
        }}
      >
        <CardBody style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', paddingBottom: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, flex: 1 }}>
            <h2 style={{ marginBottom: '20px', fontSize: process.env.REACT_APP_WEBSITE === 'CLEARNET' ? '1.25rem' : '1.75rem', fontWeight: 700 }}>
              ClearStone Hub
            </h2>
            <div ref={containerRef} style={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column' }}>
              {error ? (
                <div className="text-center mt-4 text-danger">{error}</div>
              ) : folders === null ? (
                <div className="text-center mt-4 text-muted">Loading...</div>
              ) : (
                containerHeight > 0 && (
                  <Tree
                    ref={treeRef}
                    data={folders}
                    idAccessor="id"
                    childrenAccessor="children"
                    selection={selectedId}
                    onSelect={(nodes) => {
                      const node = nodes[0];
                      if (node) {
                        setSelectedId(String(node.id));
                      }
                      // If root node is selected, treat as null (no folder selected)
                      if (node && folders && folders.length > 0 && String(node.id) === String(folders[0].id)) {
                        onFolderChange(null);
                      } else if (node) {
                        onFolderChange(node.id);
                      }
                    }}
                    // --- INDENTATION SETTINGS ---
                    indent={24}
                    rowHeight={32}
                    width="100%"
                    height={containerHeight}
                  >
                    {Node}
                  </Tree>
                )
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    </React.Fragment>
  );
});

FolderTree.displayName = "FolderTree";

export default FolderTree;